import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import styled from "styled-components/macro";

import { faPencil } from "@fortawesome/pro-regular-svg-icons";
import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";
import {
  endDateText,
  startDateText,
} from "Payments/Shared/RecurringDatePicker";
import RecurringIntervalOptions from "Payments/Shared/RecurringIntervalOptions";
import INTERVAL from "Payments/Shared/intervalEnum";
import { AnimatePresence, motion } from "framer-motion";
import { DateTime } from "luxon";
import qs from "qs";

import FnrCheckbox from "UI/FnrEnrichedCheckbox/FnrCheckbox";

import FnrFetch3 from "Utilities/FnrFetch/FnrFetch3";

import { usePaymentForm } from "../../PaymentFormContext";

import ExistingRecurringPayments from "./ExistingRecurringPayments";
import RecurringDates from "./RecurringDates";
import TotalAmount from "./TotalAmount";
import useRecurringPaymentState from "./useRecurringPaymentState";
import {
  getValidEndDate,
  getValidInterval,
  getValidStartDate,
} from "./validDatehelper";

export default function RecurringPaymentSection() {
  const {
    state: {
      amount,
      recurringPayment,
      fund: { fundColorSafe },
      paymentParent,
    },
    dispatch,
  } = usePaymentPage();
  const {
    state: {
      formStages: { auth },
    },
    dispatch: formDispatch,
  } = usePaymentForm();

  const location = useLocation();
  const { pledgeID: pledgeIDParam, signupListID } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  });

  const [suggestionsSet, setSuggestionsSet] = useState(false);
  const [totalToGive, setTotalToGive] = useState();

  const suggestedRecurring = window?.data?.paymentPageData?.suggestedRecurring;
  const pledge = window?.data?.paymentPageData?.campaign?.pledge;
  const hasExistingRecurringGiftForFund =
    window?.data?.paymentPageData?.user?.hasExistingRecurringGiftForFund;

  const { expanded, fullSize } = useRecurringPaymentState();

  const setFrequency = useCallback(
    (selection) => {
      dispatch({ type: "selectRecurrenceFrequency", payload: selection });
    },
    [dispatch]
  );

  const toggle = useCallback(
    function setDefaultRecurringState() {
      if (!recurringPayment.frequency) {
        setFrequency({ id: "monthly", label: "monthly" });
      }
      if (!recurringPayment.startDate) {
        let today = DateTime.now().toISODate();
        dispatch({ type: "setStartDate", payload: today });
      }

      dispatch({ type: "toggleRecurringPayment" });

      if (auth === "skipped" && !recurringPayment.recurring) {
        formDispatch({
          type: "setFormStage",
          stage: "auth",
          value: "requestCode",
        });
      }
    },
    [dispatch, auth, recurringPayment, formDispatch, setFrequency]
  );

  const setEditing = (bool) => {
    dispatch({ type: "setEditingRecurringPayment", payload: bool });
  };

  useEffect(
    function setSuggestedRecurring() {
      if (suggestionsSet) return;

      const hasSuggestedData =
        suggestedRecurring?.startDate ||
        suggestedRecurring?.endDate ||
        suggestedRecurring?.interval;

      const hasPledgeSchedule =
        pledge?.startDate || pledge?.endDate || pledge?.interval;

      // don't set default recurring data if the pledge is for a one-time payment
      if (pledge?.interval?.toLowerCase() === "one-time") return;

      // don't set default recurring data if there is no data to use
      if (
        !hasSuggestedData &&
        !hasPledgeSchedule &&
        !paymentParent?.recurringAlwaysOn
      )
        return;

      // don't set existing pledge data if there is no pledgeID query parameter
      if (pledge && !pledgeIDParam && !hasSuggestedData) return;

      // get valid recurring data in preparation to set it in dispatch below
      let validStartDate = getValidStartDate(
        suggestedRecurring?.startDate || pledge?.startDate
      );
      let validEndDate = getValidEndDate(
        suggestedRecurring?.endDate || pledge?.endDate
      );
      let validInterval = getValidInterval(
        suggestedRecurring?.interval || pledge?.interval.toLowerCase()
      );
      dispatch({
        type: "setSuggestedRecurringData",
        startDate: validStartDate,
        endDate: validEndDate,
        frequency: validInterval,
      });
      setSuggestionsSet(true);

      dispatch({
        type: "updateRecurringPayment",
        payload: {
          recurring: true,
          editing: true,
        },
      });
    },
    [
      suggestedRecurring,
      pledge,
      suggestionsSet,
      dispatch,
      toggle,
      pledgeIDParam,
      paymentParent,
    ]
  );

  const getSettingsHeight = ({
    hasExistingRecurringGiftForFund,
    hasEndDate,
    hasAmount,
  }) => {
    let totalHeight = 50;
    let hasExistingRecurringGiftHeight = 50;
    let totalAmountHeight = 38;

    if (hasExistingRecurringGiftForFund) {
      totalHeight += hasExistingRecurringGiftHeight;
    }
    if (hasEndDate && hasAmount) {
      totalHeight += totalAmountHeight;
    }

    return totalHeight;
  };

  const getTotalToGive = useCallback(async () => {
    let { startDate, endDate, frequency } = recurringPayment;

    if (!endDate || !amount || amount <= 0) {
      setTotalToGive(null);
      return;
    }

    try {
      let startD = DateTime.fromISO(startDate);
      let endD = DateTime.fromISO(endDate);
      const data = {
        startDate: startD.toMillis() / 1000,
        endDate: endD.toMillis() / 1000,
        intervalID: INTERVAL[frequency.toUpperCase()].ID,
        amount: amount * 100,
      };
      const fetch = await new FnrFetch3("/give/getTotalAmountToBeGiven", data);
      const res = await fetch.post();
      setTotalToGive(res.totalAmount);
    } catch (err) {
      console.log(err);
    }
  }, [amount, recurringPayment]);

  useEffect(
    function updateTotalToGive() {
      getTotalToGive();
    },
    [amount, recurringPayment, getTotalToGive]
  );

  if (window?.data?.paymentPageData?.hasRecurringOn === false) return null;
  if (window.location.search.includes("recurringGiftID")) return null;
  if (signupListID) return null;

  return (
    <div>
      <Wrapper
        animate={{
          scale: fullSize ? 1 : 0.85,
          originX: 0,
          opacity: fullSize ? 1 : 0.85,
        }}
        transition={{ delay: fullSize ? 0 : 0.2 }}
        $fullSize={fullSize}
      >
        {!recurringPayment.editing && recurringPayment.recurring ? (
          <div style={{ width: fullSize ? "100%" : "120%", color: "" }}>
            <span>
              Recurs {recurringPayment.frequency} starting{" "}
              {startDateText(recurringPayment.startDate)} and going until{" "}
              {endDateText(recurringPayment.endDate)}{" "}
            </span>
            <RecurringSummaryButton
              onClick={() => setEditing(true)}
              color={fundColorSafe}
            >
              <FontAwesomeIcon
                icon={faPencil}
                style={{ marginRight: "4px", color: fundColorSafe }}
                size="1x"
              />
              Edit
            </RecurringSummaryButton>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center" }}>
            {paymentParent?.recurringAlwaysOn ? (
              <div>Recurring </div>
            ) : (
              <FnrCheckbox
                on={recurringPayment.recurring}
                toggleOn={toggle}
                color={fundColorSafe}
              >
                Make recurring{" "}
              </FnrCheckbox>
            )}
            <AnimatePresence>
              {expanded ? (
                <motion.div
                  style={{ display: "flex", alignItems: "center" }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {`: `}
                  <RecurringIntervalOptions
                    selected={{
                      id: recurringPayment?.frequency || "monthly",
                      label: recurringPayment?.frequency || "monthly",
                    }}
                    onSelect={setFrequency}
                    fundColor={fundColorSafe}
                  />
                </motion.div>
              ) : (
                ""
              )}
            </AnimatePresence>
          </div>
        )}
        {/* <ExistingRecurringPayments /> */}
        <AnimatePresence>
          {expanded ? (
            <SettingsWrapper
              initial={{ opacity: 0, height: 0 }}
              animate={{
                opacity: 1,
                height: getSettingsHeight({
                  hasExistingRecurringGiftForFund,
                  hasEndDate: recurringPayment?.endDate,
                  hasAmount: amount > 0,
                }),
                transition: { type: "tween" },
              }}
              exit={{ opacity: 0, height: 0, transition: { type: "tween" } }}
            >
              <RecurringDates />

              <TotalAmount totalAmount={totalToGive} />

              <ExistingRecurringPayments />
            </SettingsWrapper>
          ) : null}
        </AnimatePresence>
      </Wrapper>
    </div>
  );
}

const Wrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 6px;
  margin-top: ${(props) => (props.$fullSize ? "6px" : 0)};
  font-weight: 400;
  color: hsl(0, 0%, 30%);
`;

const SettingsWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Frequency = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-left: 28px;

  @media screen and (max-width: 480px) {
    margin-left: 0;
  }
`;

const Button = styled.button`
  /* color: #525151; */
  background: ${(props) =>
    props.selected && props.color ? props.color : "transparent"};
  color: ${(props) => (props.selected ? "white" : "inherit")};
  border: 1px solid hsl(0 0% 90%);
  padding: 4px;
  margin: 0;
  border-radius: 4px;
  font-size: 13px;

  .inside {
    font-weight: ${(props) => (props.selected ? 600 : 600)};
    transition: transform 0.3s ease-in-out;
    text-transform: capitalize;
  }

  &:hover {
    cursor: pointer;
    background: ${(props) =>
      props.selected && props.color ? props.color : "hsl(0 0% 92%)"};

    .inside {
      transform: scale(1.1);
    }
  }
`;

const RecurringSummaryButton = styled.button`
  --color: hsl(225, 72%, 39%);
  background: transparent;
  border: none;
  color: ${(props) => (props.color ? props.color : `var(--color)`)};
  font-size: inherit;
  font-weight: 600;
  transition: transform 0.2s ease-in-out;

  &:hover {
    cursor: pointer;
    transform: scale(1.1);
  }
`;

const FreqButton = ({
  frequency,
  selectedFrequency,
  onClick,
  color,
  children,
}) => {
  return (
    <Button
      onClick={(e) => onClick(e, frequency)}
      selected={selectedFrequency === frequency}
      color={color}
    >
      <div className="inside">{frequency}</div>
    </Button>
  );
};
